import { cmsApi, CmsApiError } from "../api/cmsApi";
import { isCmsCacheFresh, useCmsStore } from "../store/cmsStore";
import {
  validateCompany,
  validateFounder,
  validatePortfolioInput,
  validateSocialLinks,
  validateStats,
} from "../validation/cmsValidation";
import type {
  CmsCompanyInfo,
  CmsFounder,
  CmsHeroCta,
  CmsHomepage,
  CmsPortfolioInput,
  CmsPortfolioProject,
  CmsSocialLink,
  CmsSocialPlatform,
  CmsStatItem,
  CmsVisitorAnalytics,
  CmsWorkspaceOverview,
} from "../types";

function sortStats(stats: CmsStatItem[]) {
  return [...stats].sort((a, b) => a.order - b.order);
}

function invalidateWorkspace() {
  useCmsStore.getState().invalidate("dashboard");
  useCmsStore.getState().invalidate("workspace");
}

export const cmsService = {
  async getDashboard() {
    const store = useCmsStore.getState();
    if (store.dashboard.data && isCmsCacheFresh(store.dashboard.fetchedAt)) {
      return store.dashboard.data;
    }
    store.setResourceLoading("dashboard");
    try {
      const data = await cmsApi.dashboard();
      store.setResourceSuccess("dashboard", data);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load dashboard";
      store.setResourceError("dashboard", message);
      throw error;
    }
  },

  async getWorkspaceOverview(): Promise<CmsWorkspaceOverview> {
    const store = useCmsStore.getState();
    if (store.workspace.data && isCmsCacheFresh(store.workspace.fetchedAt)) {
      return store.workspace.data;
    }
    store.setResourceLoading("workspace");
    try {
      const data = await cmsApi.workspace();
      store.setResourceSuccess("workspace", data);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load workspace";
      store.setResourceError("workspace", message);
      throw error;
    }
  },

  async getHomepage(): Promise<CmsHomepage> {
    const store = useCmsStore.getState();
    if (store.homepage.data && isCmsCacheFresh(store.homepage.fetchedAt)) {
      return store.homepage.data;
    }
    store.setResourceLoading("homepage");
    try {
      const { homepage } = await cmsApi.getHomepage();
      const sorted = { ...homepage, stats: sortStats(homepage.stats) };
      store.setResourceSuccess("homepage", sorted);
      return sorted;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load homepage";
      store.setResourceError("homepage", message);
      throw error;
    }
  },

  async updateHomepage(patch: Partial<CmsHomepage>) {
    if (patch.stats) {
      const statsError = validateStats(patch.stats);
      if (statsError) throw new Error(statsError);
    }
    const { homepage } = await cmsApi.updateHomepage(patch);
    const sorted = { ...homepage, stats: sortStats(homepage.stats) };
    useCmsStore.getState().setResourceSuccess("homepage", sorted);
    invalidateWorkspace();
    return sorted;
  },

  async setFeaturedProjects(ids: string[]) {
    return cmsService.updateHomepage({ featuredProjectIds: ids });
  },

  async updateHeroCta(heroCta: CmsHeroCta) {
    const homepage = await cmsService.updateHomepage({ heroCta });
    return homepage.heroCta;
  },

  async getPortfolio(): Promise<CmsPortfolioProject[]> {
    const store = useCmsStore.getState();
    if (store.portfolio.data && isCmsCacheFresh(store.portfolio.fetchedAt)) {
      return store.portfolio.data;
    }
    store.setResourceLoading("portfolio");
    try {
      const { portfolio } = await cmsApi.getPortfolio();
      store.setResourceSuccess("portfolio", portfolio);
      return portfolio;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load portfolio";
      store.setResourceError("portfolio", message);
      throw error;
    }
  },

  async getProject(id: string): Promise<CmsPortfolioProject | null> {
    try {
      const { project } = await cmsApi.getProject(id);
      return project;
    } catch (error) {
      if (error instanceof CmsApiError && error.status === 404) return null;
      throw error;
    }
  },

  async createProject(input: CmsPortfolioInput) {
    const validationError = validatePortfolioInput(input, true);
    if (validationError) throw new Error(validationError);
    const { project } = await cmsApi.createProject(input);
    const store = useCmsStore.getState();
    const current = store.portfolio.data ?? [];
    store.setResourceSuccess("portfolio", [project, ...current]);
    invalidateWorkspace();
    return project;
  },

  async updateProject(id: string, patch: Partial<CmsPortfolioInput>) {
    const validationError = validatePortfolioInput(patch);
    if (validationError) throw new Error(validationError);
    const { project } = await cmsApi.updateProject(id, patch);
    const store = useCmsStore.getState();
    const current = store.portfolio.data ?? [];
    store.setResourceSuccess(
      "portfolio",
      current.map((p) => (p.id === id ? project : p))
    );
    invalidateWorkspace();
    return project;
  },

  async deleteProject(id: string) {
    await cmsApi.deleteProject(id);
    useCmsStore.getState().invalidate("portfolio");
    useCmsStore.getState().invalidate("homepage");
    invalidateWorkspace();
  },

  async reorderPortfolio(ids: string[]) {
    const { portfolio } = await cmsApi.reorderPortfolio(ids);
    useCmsStore.getState().setResourceSuccess("portfolio", portfolio);
    invalidateWorkspace();
    return portfolio;
  },

  async getFounder(): Promise<CmsFounder> {
    const store = useCmsStore.getState();
    if (store.founder.data && isCmsCacheFresh(store.founder.fetchedAt)) {
      return store.founder.data;
    }
    store.setResourceLoading("founder");
    try {
      const { founder } = await cmsApi.getFounder();
      store.setResourceSuccess("founder", founder);
      return founder;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load founder";
      store.setResourceError("founder", message);
      throw error;
    }
  },

  async updateFounder(patch: Partial<CmsFounder>) {
    const current = await cmsService.getFounder();
    const merged = { ...current, ...patch };
    const validationError = validateFounder(merged);
    if (validationError) throw new Error(validationError);
    const { founder } = await cmsApi.updateFounder(merged);
    useCmsStore.getState().setResourceSuccess("founder", founder);
    invalidateWorkspace();
    return founder;
  },

  async getCompany(): Promise<CmsCompanyInfo> {
    const store = useCmsStore.getState();
    if (store.company.data && isCmsCacheFresh(store.company.fetchedAt)) {
      return store.company.data;
    }
    store.setResourceLoading("company");
    try {
      const { company } = await cmsApi.getCompany();
      store.setResourceSuccess("company", company);
      return company;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load company";
      store.setResourceError("company", message);
      throw error;
    }
  },

  async updateCompany(patch: Partial<CmsCompanyInfo>) {
    const current = await cmsService.getCompany();
    const merged = { ...current, ...patch };
    const validationError = validateCompany(merged);
    if (validationError) throw new Error(validationError);
    const { company } = await cmsApi.updateCompany(merged);
    useCmsStore.getState().setResourceSuccess("company", company);
    invalidateWorkspace();
    return company;
  },

  async getSocial(): Promise<CmsSocialLink[]> {
    const store = useCmsStore.getState();
    if (store.social.data && isCmsCacheFresh(store.social.fetchedAt)) {
      return store.social.data;
    }
    store.setResourceLoading("social");
    try {
      const { social } = await cmsApi.getSocial();
      store.setResourceSuccess("social", social);
      return social;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load social links";
      store.setResourceError("social", message);
      throw error;
    }
  },

  async saveSocial(links: CmsSocialLink[]) {
    const validationError = validateSocialLinks(links);
    if (validationError) throw new Error(validationError);
    const { social } = await cmsApi.saveSocial(links);
    useCmsStore.getState().setResourceSuccess("social", social);
    invalidateWorkspace();
    return social;
  },

  async addSocial(platform: CmsSocialPlatform, profileUrl: string) {
    const links = await cmsService.getSocial();
    const link: CmsSocialLink = {
      id: `soc-${Date.now()}`,
      platform,
      profileUrl,
      visible: true,
    };
    return cmsService.saveSocial([...links, link]);
  },

  async uploadFile(file: File) {
    return cmsApi.uploadFile(file);
  },

  async getAnalytics(range: "today" | "7d" | "30d" = "30d"): Promise<CmsVisitorAnalytics> {
    const store = useCmsStore.getState();
    store.setResourceLoading("analytics");
    try {
      const { analytics } = await cmsApi.getAnalytics(range);
      store.setResourceSuccess("analytics", analytics);
      return analytics;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load analytics";
      store.setResourceError("analytics", message);
      throw error;
    }
  },

  async search(query: string) {
    const { results } = await cmsApi.search(query);
    return results;
  },

  async publish() {
    const result = await cmsApi.publish();
    useCmsStore.getState().invalidateAll();
    invalidateWorkspace();
    return result;
  },

  async unpublish() {
    const result = await cmsApi.unpublish();
    useCmsStore.getState().invalidateAll();
    invalidateWorkspace();
    return result;
  },
};
